import {Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Question} from '../../../data/models/test/question';
import {QUESTION_VALIDATORS} from '../../../../constants/validators';
import {QuestionService} from '../../../services/api/question.service';
import {QUESTION_TYPES} from 'src/constants/questionTypes';
import {FormBuilder, FormGroup} from '@angular/forms';
import {MatDialog} from '@angular/material/dialog';
import {QuestionDeleteDialogComponent} from '../../dialogs/question-delete-dialog/question-delete-dialog.component';
import {QuestionTypeService} from 'src/app/services/api/question-type.service';
import {QuestionType} from 'src/app/data/models/questionType';

@Component({
  selector: 'app-question-edit-form',
  templateUrl: './question-edit-form.component.html',
  styleUrls: ['./question-edit-form.component.css']
})
export class QuestionEditFormComponent implements OnInit {

  readonly QUESTION_TYPES = QUESTION_TYPES;

  @Input() readonly!: boolean;
  @Input() index!: number;
  @Input() testId!: number;
  @Input() testType!: string;
  @Input() question!: Question;

  @ViewChild('input') inputElement!: ElementRef;

  @Output() delete = new EventEmitter<void>();

  public form!: FormGroup;
  public editMode = false;

  questionTypesToChange: QuestionType[] = [];
  categories: Map<string, string> = new Map<string, string>();

  originalQuestion: Question | undefined;

  constructor(
    public dialog: MatDialog,
    private fb: FormBuilder,
    private questionService: QuestionService,
    private questionTypeService: QuestionTypeService,
  ) {
      this.getQuestionTypesToChange();
      this.getQuestionCategories();
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      text: [this.question.text, QUESTION_VALIDATORS.TEXT],
      type: [this.question.type.techId, QUESTION_VALIDATORS.TYPE],
      category: [this.question.category],
      minAnswers: [this.question.minAnswers ?? null, QUESTION_VALIDATORS.MIN_ANSWERS],
      maxAnswers: [this.question.maxAnswers ?? null, QUESTION_VALIDATORS.MAX_ANSWERS],
    }, {validators: [QUESTION_VALIDATORS.MIN_MAX_RANGE('minAnswers', 'maxAnswers')]});
    this.getQuestionTypesToChange();
    this.getQuestionCategories();
    this.form.controls.type.valueChanges.subscribe(value => this.onQuestionTypeValueChange(value));
    this.onQuestionTypeValueChange(this.form.controls.type.value);
  }

  getQuestionTypesToChange(): void {
    this.questionTypeService.questionTypes$
      .subscribe(questionTypes => this.questionTypesToChange = questionTypes.filter(type => type.techId !== QUESTION_TYPES.TEXT_ANSWER));
  }

  getQuestionCategories(): void {
      this.questionTypeService.questionCategories$
          .subscribe(questionCategories => this.categories = questionCategories);
  }

  private onQuestionTypeValueChange(questionTypeTechId: string | null): void {
    if (questionTypeTechId === QUESTION_TYPES.MULTIPLE_ANSWER) {
      this.restoreMinMaxValues();
    } else {
      this.clearMinMaxValues(true);
    }
  }

  private clearMinMaxValues(updateQuestion: boolean): void {
    this.form.controls.minAnswers.setValue(null, {emitEvent: false});
    this.form.controls.maxAnswers.setValue(null, {emitEvent: false});
    this.form.controls.minAnswers.updateValueAndValidity({emitEvent: false});
    this.form.controls.maxAnswers.updateValueAndValidity({emitEvent: false});
    this.form.updateValueAndValidity({onlySelf: false, emitEvent: false});
    if (updateQuestion) {
      this.question.minAnswers = null;
      this.question.maxAnswers = null;
    }
  }

  private restoreMinMaxValues(): void {
    this.form.controls.minAnswers.setValue(this.question.minAnswers ?? null, {emitEvent: false});
    this.form.controls.maxAnswers.setValue(this.question.maxAnswers ?? null, {emitEvent: false});
    this.form.controls.minAnswers.updateValueAndValidity({emitEvent: false});
    this.form.controls.maxAnswers.updateValueAndValidity({emitEvent: false});
    this.form.updateValueAndValidity({onlySelf: false, emitEvent: false});
  }

  setEditMode(value: boolean): void {
    this.editMode = value;
    if (value) {
      setTimeout(() => {
        this.inputElement.nativeElement.focus();
      }, 0);
    }
  }

  onEdit(event: MouseEvent): void {
    event.preventDefault();
    if (!this.readonly) {
      this.setEditMode(true);
    }
  }

  onDelete(event: MouseEvent): void {
    event.preventDefault();
    if (!this.readonly) {
      this.openQuestionDeleteDialog();
    }
  }

  onSubmit(event: MouseEvent): void {
    event.preventDefault();
    if (this.form.valid) {
      const text = this.form.controls.text.value;
      const typeTechId = this.form.controls.type.value;
      const category = this.form.controls.category.value;
      if (this.question.text === text && !this.originalQuestion) {
        this.setEditMode(false);
      } else {
        let correctOptionIds: Number[] | undefined;
        let imageFile = this.image;
        const minAnswers = this.getControlNumberValue('minAnswers');
        const maxAnswers = this.getControlNumberValue('maxAnswers');
        const isMultipleAnswer = typeTechId === QUESTION_TYPES.MULTIPLE_ANSWER;

        if (this.question.options != null) {
          correctOptionIds = this.question.options.filter(option => option.isCorrect).map(option => option.id);
        }

        this.questionService.updateQuestion(this.testId, this.question.id, {
          text,
          typeTechId,
          category,
          correctOptionIds,
          imageFile,
          minAnswers: isMultipleAnswer ? minAnswers : undefined,
          maxAnswers: isMultipleAnswer ? maxAnswers : undefined,
        })
          .then(question => {
            this.question = question;
            this.setEditMode(false);
            this.originalQuestion = undefined;
            this.questionService.getImage(question.imageName).subscribe(image => {this.createImage(image, this.question)});
            this.form.controls.text.setValue(this.question.text);
            this.form.controls.type.setValue(this.question.type.techId);
            this.form.controls.category.setValue(this.question.category);
            this.form.controls.minAnswers.setValue(this.question.minAnswers ?? null);
            this.form.controls.maxAnswers.setValue(this.question.maxAnswers ?? null);
            this.onQuestionTypeValueChange(this.question.type.techId);
          });
      }
    }
  }

  onCancel(event: MouseEvent): void {
    event.preventDefault();
    if(this.originalQuestion) {
      this.question = this.originalQuestion;
      this.originalQuestion = undefined;
      this.image = undefined;
      this.imageInput.nativeElement.value = null;
    }
    this.form.patchValue({
      text: this.question.text,
      type: this.question.type.techId,
      category: this.question.category,
      minAnswers: this.question.minAnswers ?? null,
      maxAnswers: this.question.maxAnswers ?? null,
    });
    this.onQuestionTypeValueChange(this.question.type.techId);
    this.setEditMode(false);
  }

  openQuestionDeleteDialog(): void {
    const dialogRef = this.dialog.open(QuestionDeleteDialogComponent, {
      data: {
        testId: this.testId,
        question: this.question,
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.delete.emit();
      }
    });
  }

  onQuestionTypeChange(questionTypeTechId: string): void {
    const questionType = this.questionTypesToChange.find(type => type.techId === questionTypeTechId);
    if(questionType) {
      this.copyQuestionObject();

      this.question.type = questionType;

      if (this.question.options) {
        this.question.options.forEach(option => option.isCorrect = false);
      }
      this.onQuestionTypeValueChange(questionType.techId);
    }
  }

  onQuestionCategoryChange(questionCategory: string): void {
    const categories = new Map(Object.entries(this.categories));
    if(categories.has(questionCategory)) {
        this.copyQuestionObject();

        this.question.category = questionCategory;
    }
  }

    image: File | undefined;
    @ViewChild('imageInput') imageInput!: ElementRef;

    uploadImageFile(imageInput: any) {
        this.copyQuestionObject();

        const file: File = imageInput.files[0];
        const reader = new FileReader();

        reader.addEventListener('load', () => {
            this.question.imageFile = reader.result;
        });

        reader.readAsDataURL(file);
        this.image = file;
    }

    deleteLoadedImage() {
        this.copyQuestionObject();

        this.image = undefined;
        this.imageInput.nativeElement.value = null;
        this.question.imageFile = null;
    }

    private getControlNumberValue(controlName: 'minAnswers' | 'maxAnswers'): number | undefined {
        const value = this.form.controls[controlName].value;
        if (value === null || value === undefined || value === '') {
            return undefined;
        }
        const parsedValue = Number(value);
        return Number.isNaN(parsedValue) ? undefined : parsedValue;
    }

    private createImage(image: Blob, question: Question) {
        if (image && image.size > 0) {
            const reader = new FileReader();

            reader.addEventListener("load", () => {
                    question.imageFile = reader.result;
            }, false);

            reader.readAsDataURL(image);
        }
    }


    private copyQuestionObject(): void {
        if (!this.originalQuestion) {
            this.originalQuestion =  Object.assign({}, this.question);

            if (this.question.options) {
                this.originalQuestion.options = this.question.options.map(option => ({
                    ...option,
                    blockedOptionIds: option.blockedOptionIds ? [...option.blockedOptionIds] : [],
                }));
            }
        }
    }
}
